/* eslint-disable no-console */
import { PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import * as EmptyState from '~/components/EmptyState'
import { MasonryGrid } from '~/components/MasonryGrid'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { Button, PlusSquares } from '~/ui'
import { cn } from '~/utils/classnames'

import { useCollectionConfiguration } from './collection-configuration-context'
import { CollectionProductCard } from './collection-product-card'
import type { CollectionItemFormData } from './type'

type CollectionProductsProps = {
  onAddProduct: () => void
}

export const CollectionProducts = ({
  onAddProduct,
}: CollectionProductsProps) => {
  const { t } = useTranslation()
  const seller = useOptionalSeller()

  const { dispatch, collectionItems } = useCollectionConfiguration()

  const onRemoveCollectionItem = (item: CollectionItemFormData) => {
    const newCollectionItems = collectionItems.filter(
      (i) => i.productId !== item.productId
    )

    dispatch({
      type: 'patch-collection-items',
      payload: newCollectionItems,
    })
  }

  return (
    <>
      <div className="flex h-full w-full flex-1 px-2 @sm:px-6">
        {collectionItems.length > 0 ? (
          <div className="relative my-8 h-full w-full">
            <div className="text-foreground/80 mb-5 flex items-center gap-1 text-sm">
              <PlusSquares />
              <p className="font-medium">
                {t(
                  collectionItems.length > 1
                    ? 'page.collections.save_collection_items.collection_item_count.n_product'
                    : 'page.collections.save_collection_items.collection_item_count.1_product',
                  { count: collectionItems.length }
                )}
              </p>
            </div>

            <MasonryGrid>
              {collectionItems.map((item) => {
                return (
                  <CollectionProductCard
                    key={item.product.id}
                    product={item.product}
                    onRemove={() => onRemoveCollectionItem(item)}
                  />
                )
              })}
            </MasonryGrid>
          </div>
        ) : (
          <div className="relative flex h-[60dvh] w-full items-center justify-center overflow-hidden px-2 @sm:px-6">
            <div className="z-10 max-w-[347px] text-center">
              <EmptyState.Product className="mx-auto" />
              <div className="mx-auto mt-8 text-sm font-medium">
                <h2 className="text-foreground">
                  {t(
                    'page.collections.save_collection_items.empty_state.title'
                  )}
                </h2>

                {seller && (
                  <>
                    <p className="text-foreground/64 mt-2">
                      {t(
                        'page.collections.save_collection_items.empty_state.subtitle'
                      )}
                    </p>

                    <div className="mt-5">
                      <Button
                        size="lg"
                        variant="primary"
                        className="cursor-pointer"
                        onClick={onAddProduct}
                      >
                        <PlusIcon className="opacity-64" />
                        {t('action.add_product')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
            <BackgroundTiles />
          </div>
        )}
      </div>
    </>
  )
}

const BackgroundTiles = () => (
  <div className="absolute top-0 left-0 h-full w-full">
    <div className="from-background to-background/8 absolute top-0 block h-1/2 w-full bg-linear-to-b from-40% to-100%" />

    <div className="grid grid-cols-2 gap-3 pt-24 sm:grid-cols-3 sm:gap-6">
      {Array.from({ length: 3 }, (_, i) => i + 1).map((columnNumber) => (
        <div className="flex flex-1 flex-col gap-6" key={columnNumber}>
          {Array.from({ length: 3 }, (_, i) => i + 1).map((i) => (
            <div className={cn('bg-under/2 flex h-96 rounded-md')} key={i} />
          ))}
        </div>
      ))}
    </div>
  </div>
)
